// Newsletter coloured text box block.
//
// Schema:
//   newsletterTextBox  content: "newsletterText+"
//   attrs: id, backgroundColor
//
// Background colour is a node attr; text colour inside is the existing
// textStyle Color mark, so no extra plumbing is needed for text. The NodeView
// renders a floating swatch row (presets + custom colour input) that shows
// while the box is active — same affordance pattern as image_text's
// layout toggle. The email renderer emits a table cell with bgcolor so the
// background survives Outlook.

import { Node, mergeAttributes } from "@tiptap/core"

import { idAttributeSpec } from "./attributes"
import { insertBlockAfter } from "./commands"

export const TEXT_BOX_DEFAULT_BG = "#f3f4f6"

// Recognisable colour presets. Custom input below covers everything else.
const PRESET_COLORS = [
  "#f3f4f6", // light gray (default)
  "#ef4444", // red
  "#f97316", // orange
  "#facc15", // yellow
  "#22c55e", // green
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
]

export const NewsletterTextBox = Node.create({
  name: "newsletterTextBox",
  group: "newsletterBlock",
  content: "newsletterText+",
  draggable: true,
  selectable: true,
  isolating: true,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addAttributes() {
    return {
      id: idAttributeSpec,
      backgroundColor: {
        default: TEXT_BOX_DEFAULT_BG,
        parseHTML: (el) => el.getAttribute("data-background-color") || TEXT_BOX_DEFAULT_BG,
        // Inline style keeps the colour in the cached legacy `body` HTML,
        // where the data-* attrs get stripped by sanitize.
        renderHTML: (attrs) => ({
          "data-background-color": attrs.backgroundColor,
          style: `background-color: ${attrs.backgroundColor}`,
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "div[data-newsletter-text-box]" }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-newsletter-text-box": "",
      }),
      0,
    ]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => buildTextBoxView(node, editor, getPos)
  },

  addCommands() {
    return {
      insertNewsletterTextBox:
        (attrs = {}) =>
        (props) =>
          insertBlockAfter(this.name, attrs)(props),

      setTextBoxColor:
        (pos, backgroundColor) =>
        ({ state, dispatch, tr }) => {
          const node = state.doc.nodeAt(pos)
          if (!node || node.type.name !== "newsletterTextBox") return false
          if (!dispatch) return true
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, backgroundColor })
          dispatch(tr)
          return true
        },
    }
  },
})

function buildTextBoxView(initialNode, editor, getPos) {
  const dom = document.createElement("div")
  dom.setAttribute("data-newsletter-text-box", "")
  dom.className = "nl-text-box"

  const content = document.createElement("div")
  content.className = "nl-text-box__content"

  const swatches = document.createElement("div")
  swatches.className = "nl-text-box__swatches"
  swatches.contentEditable = "false"

  // `focus: false` while the native colour picker is open — re-focusing the
  // editor mid-pick blurs the input and the browser closes the picker.
  const applyColor = (color, { focus = true } = {}) => {
    const pos = typeof getPos === "function" ? getPos() : null
    if (pos === null || pos === undefined) return
    if (focus) {
      editor.chain().focus().setTextBoxColor(pos, color).run()
    } else {
      editor.commands.setTextBoxColor(pos, color)
    }
  }

  PRESET_COLORS.forEach((color) => {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "nl-text-box__swatch"
    btn.dataset.color = color
    btn.style.background = color
    btn.title = color
    // Keep the editor selection — a default mousedown would blur the caret
    // out of the box and collapse the swatch row before click lands.
    btn.addEventListener("mousedown", (event) => event.preventDefault())
    btn.addEventListener("click", (event) => {
      event.preventDefault()
      applyColor(color)
    })
    swatches.appendChild(btn)
  })

  const custom = document.createElement("input")
  custom.type = "color"
  custom.className = "nl-text-box__custom"
  custom.title = "Custom color…"
  // Take focus before the click opens the native picker — :focus-within
  // then keeps the swatch row visible while the editor itself is blurred.
  custom.addEventListener("mousedown", () => custom.focus())
  // Live preview while the picker is open; settle focus back into the
  // editor once the picker closes (change fires on close).
  custom.addEventListener("input", () => applyColor(custom.value, { focus: false }))
  custom.addEventListener("change", () => applyColor(custom.value))
  swatches.appendChild(custom)

  dom.append(swatches, content)

  const refresh = (node) => {
    const color = node.attrs.backgroundColor || TEXT_BOX_DEFAULT_BG
    dom.style.backgroundColor = color
    dom.setAttribute("data-background-color", color)
    if (/^#[0-9a-fA-F]{6}$/.test(color)) custom.value = color
    swatches.querySelectorAll(".nl-text-box__swatch").forEach((btn) => {
      btn.dataset.active = btn.dataset.color === color ? "true" : "false"
    })
  }

  refresh(initialNode)

  return {
    dom,
    contentDOM: content,
    update(node) {
      if (node.type.name !== initialNode.type.name) return false
      refresh(node)
      return true
    },
    selectNode() {
      dom.classList.add("nl-text-box--selected")
    },
    deselectNode() {
      dom.classList.remove("nl-text-box--selected")
    },
    ignoreMutation(mutation) {
      return !content.contains(mutation.target)
    },
    // The swatch row owns its events — without this PM treats the click as
    // an editor interaction, grabs focus, and the native picker never opens.
    stopEvent(event) {
      return swatches.contains(event.target)
    },
  }
}
